import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { formatDate, formatNumber } from '../ui/format';
import { Badge, Loading, PageHeader, Panel, type BadgeTone } from '../ui/Page';
import { DataTable, type Column } from '../ui/DataTable';
import { formAnalytics, listForms, type FormView } from './api';
import { createForm, keyFrom } from './forms-api';

/**
 * Forms, surveys and NPS (context.md sections 66-71).
 *
 * The list answers "is it collecting anything"; selecting one opens its numbers. NPS gets its own
 * figures because a promoter count is not a conversion rate, and showing one as the other would be
 * worse than showing neither.
 */

const STATUS_TONE: Record<string, BadgeTone> = {
  PUBLISHED: 'positive',
  DRAFT: 'neutral',
  ARCHIVED: 'neutral',
};

export function FormsPage(): React.JSX.Element {
  const [selected, setSelected] = useState<FormView | null>(null);

  const forms = useQuery({ queryKey: ['forms'], queryFn: () => listForms() });

  const columns: Column<FormView>[] = [
    {
      key: 'name',
      header: t('forms.name'),
      render: (form) => (
        <span className="cell-stack">
          <span className="strong">{form.name}</span>
          <span className="muted small">
            <code>/{form.slug}</code>
          </span>
        </span>
      ),
    },
    {
      key: 'kind',
      header: t('forms.kind'),
      render: (form) => <Badge tone="info">{t(`forms.type.${form.kind}`)}</Badge>,
    },
    {
      key: 'status',
      header: t('crm.field.status'),
      render: (form) => (
        <Badge tone={STATUS_TONE[form.status] ?? 'neutral'}>
          {t(`forms.state.${form.status}`)}
        </Badge>
      ),
    },
    {
      key: 'submissions',
      header: t('forms.submissions'),
      align: 'end',
      render: (form) => formatNumber(form.submissionCount),
    },
    {
      key: 'created',
      header: t('common.createdAt'),
      secondary: true,
      align: 'end',
      render: (form) => formatDate(form.createdAt),
    },
    {
      key: 'actions',
      header: '',
      align: 'end',
      render: (form) => (
        <Link
          className="button button--quiet button--compact"
          to={`/marketing/formularios/${form.id}`}
          onClick={(event) => event.stopPropagation()}
        >
          {t('forms.edit')}
        </Link>
      ),
    },
  ];

  return (
    <div className="view">
      <PageHeader
        title={t('nav.forms')}
        description={t('forms.description')}
        actions={<NewForm />}
      />

      <Panel>
        <DataTable
          tableKey="forms"
          rows={forms.data?.forms}
          columns={columns}
          rowKey={(form) => form.id}
          isLoading={forms.isLoading}
          error={forms.error}
          onRetry={() => void forms.refetch()}
          onRowClick={(form) => setSelected((current) => (current?.id === form.id ? null : form))}
          empty={{ title: t('forms.empty'), description: t('forms.emptyBody') }}
        />
      </Panel>

      {selected && <Analytics form={selected} />}
    </div>
  );
}

/**
 * A new form, from its name.
 *
 * It opens straight into the builder, empty, because a form with no questions is not something
 * anybody wants to look at in a list — the next thing to do is always to add the first one.
 */
function NewForm(): React.JSX.Element {
  const navigate = useNavigate();
  const client = useQueryClient();

  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => createForm({ name: name.trim(), slug: slugFrom(name) }),
    onSuccess: (form) => {
      setName('');
      setError(null);
      void client.invalidateQueries({ queryKey: ['forms'] });
      navigate(`/marketing/formularios/${form.id}`);
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  return (
    <form
      className="cell-inline"
      onSubmit={(event) => {
        event.preventDefault();
        if (name.trim().length >= 2) create.mutate();
      }}
    >
      <input
        value={name}
        placeholder={t('forms.newPlaceholder')}
        onChange={(event) => setName(event.target.value)}
      />

      <button
        type="submit"
        className="button button--primary button--compact"
        disabled={name.trim().length < 2 || create.isPending}
      >
        {create.isPending ? t('work.creating') : t('forms.new')}
      </button>

      {error && (
        <span className="alert small" role="alert">
          {error}
        </span>
      )}
    </form>
  );
}

/** The public slug: the same rules as a field key, with hyphens where a key takes underscores. */
function slugFrom(name: string): string {
  return keyFrom(name).replace(/_/g, '-');
}

function Analytics({ form }: { form: FormView }): React.JSX.Element {
  const analytics = useQuery({
    queryKey: ['form-analytics', form.id],
    queryFn: () => formAnalytics(form.id),
  });

  if (analytics.isLoading) {
    return (
      <Panel title={form.name}>
        <Loading />
      </Panel>
    );
  }

  const data = analytics.data;

  if (!data) {
    return (
      <Panel title={form.name}>
        <p className="muted">{t('error.generic')}</p>
      </Panel>
    );
  }

  const isNps = form.kind === 'NPS';

  return (
    <Panel title={t('forms.analyticsFor').replace('{name}', form.name)}>
      <div className="metrics">
        <Figure label={t('forms.views')} value={formatNumber(data.views)} />
        <Figure label={t('forms.submissions')} value={formatNumber(data.submissions)} />
        <Figure
          label={t('forms.conversion')}
          value={`${(data.conversionRate * 100).toFixed(1)}%`}
        />
        {data.spam !== undefined && (
          <Figure label={t('forms.spam')} value={formatNumber(data.spam)} />
        )}

        {/* NPS figures only where they mean something. */}
        {isNps && (
          <>
            <Figure
              label={t('forms.nps')}
              value={
                data.nps === null || data.nps === undefined ? '—' : String(Math.round(data.nps))
              }
            />
            <Figure label={t('forms.promoters')} value={formatNumber(data.promoters ?? 0)} />
            <Figure label={t('forms.detractors')} value={formatNumber(data.detractors ?? 0)} />
          </>
        )}
      </div>
    </Panel>
  );
}

function Figure({ label, value }: { label: string; value: string }): React.JSX.Element {
  return (
    <div className="metric">
      <p className="metric__label">{label}</p>
      <p className="metric__value">{value}</p>
    </div>
  );
}
